import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import SuperAdminStoreGate from "@/components/SuperAdminStoreGate";
import ImageUpload from "@/components/ImageUpload";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Sparkles, Megaphone, PartyPopper, LayoutTemplate,
  ImageIcon, Eye, MousePointerClick, Search, X, Loader2,
} from "lucide-react";

/**
 * Marketing — card-based campaign manager (Phase 2 redesign).
 *
 * Persists to the existing PATCH /store/settings/marketing endpoint. Each
 * banner slot lives at settings.marketing.<slot>[] with the exact same shape
 * as before so the storefront renderer needs zero changes.
 *
 * UI mirrors a Shopify/Klaviyo-style layout:
 *   • Left rail   → grouped campaign categories (Hero, Festival, Middle,
 *                   Promotion, App, Popup, System).
 *   • Main pane   → card grid with preview, active toggle, per-card view /
 *                   click analytics badges.
 *   • Right sheet → inline edit form when a card is opened.
 */

// Ordered list of slots split by group. `group` also decides the left-rail
// bucket. `aspect` drives the preview ratio.
const SLOTS = [
  { key: "home_slider",       label: "Home hero slider",  group: "hero",       aspect: "banner", accent: "from-rose-500 to-orange-500",     hint: "16:6 hero banners rotating on the customer home page." },
  { key: "top_banner",        label: "Top strip banner",  group: "hero",       aspect: "banner", accent: "from-rose-500 to-red-500",        hint: "Promotional strip pinned above the header." },
  { key: "festival_banner",   label: "Festival banners",  group: "festival",   aspect: "banner", accent: "from-amber-500 to-pink-500",      hint: "Seasonal / festival creative — auto-swap by date." },
  { key: "middle_banner",     label: "Middle banner",     group: "middle",     aspect: "banner", accent: "from-violet-500 to-fuchsia-500",  hint: "Mid-page banner between product sections." },
  { key: "brand_banner",      label: "Brand banner",      group: "middle",     aspect: "banner", accent: "from-cyan-500 to-blue-500",       hint: "Promotes a featured brand on the storefront." },
  { key: "category_banner",   label: "Category banner",   group: "promotion",  aspect: "banner", accent: "from-emerald-500 to-teal-500",    hint: "Shown on category listings and shop filters." },
  { key: "offer_banner",      label: "Offer banner",      group: "promotion",  aspect: "banner", accent: "from-fuchsia-500 to-rose-500",    hint: "Highlights active deals / discount campaigns." },
  { key: "bottom_banner",     label: "Bottom banner",     group: "promotion",  aspect: "banner", accent: "from-slate-700 to-slate-900",     hint: "Footer-adjacent banner just above the offer strip." },
  { key: "announcement_bar",  label: "Announcement bar",  group: "system",     aspect: "text",   accent: "from-indigo-500 to-blue-500",     hint: "Thin strip above the header — text focused." },
  { key: "app_banner",        label: "App promo banner",  group: "app",        aspect: "square", accent: "from-teal-500 to-emerald-500",    hint: "Square creative used inside the mobile customer app." },
  { key: "popup_banner",      label: "Popup banner",      group: "app",        aspect: "square", accent: "from-purple-500 to-indigo-500",   hint: "First-visit modal — square image works best." },
];

const GROUPS = [
  { key: "all",       label: "All campaigns", icon: Sparkles },
  { key: "hero",      label: "Hero",          icon: LayoutTemplate },
  { key: "festival",  label: "Festival",      icon: PartyPopper },
  { key: "middle",    label: "Middle",        icon: LayoutTemplate },
  { key: "promotion", label: "Promotion",     icon: Megaphone },
  { key: "app",       label: "App / Popup",   icon: LayoutTemplate },
  { key: "system",    label: "System",        icon: Sparkles },
];

const emptyItem = () => ({
  id: `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
  image: "", title: "", subtitle: "", link_to: "", is_active: true,
  view_count: 0, click_count: 0,
});

export default function Marketing() {
  return (
    <SuperAdminStoreGate
      pageTitle="Marketing"
      pageSubtitle="Card-based campaign manager for banners, festivals & popups"
    >
      <MarketingInner />
    </SuperAdminStoreGate>
  );
}

function MarketingInner() {
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState("all");
  const [query, setQuery] = useState("");
  const [editor, setEditor] = useState(null); // { slot, id } | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/settings");
      const mkt = data?.settings?.marketing || {};
      const next = {};
      SLOTS.forEach((s) => { next[s.key] = Array.isArray(mkt[s.key]) ? mkt[s.key] : []; });
      // Preserve any additional slots the backend already has.
      Object.entries(mkt).forEach(([k, v]) => { if (!(k in next)) next[k] = v; });
      setItems(next);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch("/store/settings/marketing", items);
      toast.success("Campaigns published");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const updateItem = (slot, id, patch) => setItems((s) => ({
    ...s, [slot]: (s[slot] || []).map((it) => it.id === id ? { ...it, ...patch } : it),
  }));
  const addItem = (slot) => {
    const it = emptyItem();
    setItems((s) => ({ ...s, [slot]: [...(s[slot] || []), it] }));
    setEditor({ slot, id: it.id });
  };
  const removeItem = (slot, id) => setItems((s) => ({ ...s, [slot]: (s[slot] || []).filter((it) => it.id !== id) }));

  // Filter slots by group + free-text (title / subtitle / slot key).
  const visibleSlots = useMemo(() => {
    return SLOTS.filter((s) => (group === "all" || s.group === group));
  }, [group]);

  const q = query.trim().toLowerCase();

  // For counters in the left rail.
  const totalsByGroup = useMemo(() => {
    const t = { all: 0 };
    GROUPS.forEach((g) => { if (g.key !== "all") t[g.key] = 0; });
    SLOTS.forEach((s) => {
      const c = (items[s.key] || []).length;
      t.all += c;
      t[s.group] = (t[s.group] || 0) + c;
    });
    return t;
  }, [items]);

  const openItem = editor && ((items[editor.slot] || []).find((i) => i.id === editor.id) || null);

  return (
    <div className="sa-shell min-h-[calc(100vh-56px)] bg-white" data-testid="marketing-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100 px-4 md:px-8 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Megaphone className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold leading-none">Super Admin</div>
          <div className="text-[15px] font-semibold text-slate-900 leading-tight truncate">Marketing Center</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative hidden md:block">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search campaigns…"
              className="pl-9 h-9 w-64 rounded-xl border-slate-200 bg-white text-[12.5px]"
              data-testid="marketing-search"
            />
          </div>
          <button
            type="button"
            onClick={saveAll}
            disabled={saving || loading}
            className="sa-btn-primary h-9 px-4 inline-flex items-center gap-1.5 text-[13px] disabled:opacity-60"
            data-testid="marketing-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save all"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_minmax(0,1fr)] gap-0">
        {/* Left rail — groups */}
        <aside className="border-r border-slate-100 bg-white px-3 py-5" data-testid="marketing-left-rail">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-2 mb-2">Categories</div>
          <div className="space-y-1">
            {GROUPS.map((g) => {
              const active = group === g.key;
              const count = totalsByGroup[g.key] || 0;
              const Icon = g.icon;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGroup(g.key)}
                  className={`w-full inline-flex items-center gap-2 px-2.5 h-9 rounded-xl text-[12.5px] font-semibold transition-all ${
                    active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`marketing-group-${g.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left">{g.label}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/15" : "bg-slate-100 text-slate-500"}`}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-500 leading-relaxed">
            <div className="text-[11px] font-bold text-slate-700 mb-1">Tip</div>
            Toggle any campaign on/off instantly. Analytics badges show total impressions & clicks captured by the storefront.
          </div>
        </aside>

        {/* Campaign cards */}
        <section className="p-4 md:p-8">
          {loading ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 rounded-2xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-8" data-testid="marketing-cards-pane">
              {visibleSlots.map((s) => {
                const list = (items[s.key] || []).filter((it) => {
                  if (!q) return true;
                  return (
                    (it.title || "").toLowerCase().includes(q) ||
                    (it.subtitle || "").toLowerCase().includes(q) ||
                    s.key.includes(q) || s.label.toLowerCase().includes(q)
                  );
                });
                return (
                  <div key={s.key} data-testid={`marketing-slot-${s.key}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${s.accent} text-white grid place-items-center shadow-[0_6px_20px_-8px_rgba(15,23,42,0.35)]`}>
                        <LayoutTemplate className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-slate-900 leading-tight">{s.label}</div>
                        <div className="text-[11px] text-slate-400 truncate">{s.hint}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-[10.5px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{list.length} live</span>
                        <button
                          type="button"
                          onClick={() => addItem(s.key)}
                          className="sa-btn-ghost h-8 px-2.5 inline-flex items-center gap-1 text-[12px] font-semibold"
                          data-testid={`marketing-add-${s.key}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                      </div>
                    </div>

                    {list.length === 0 ? (
                      <div
                        className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-slate-400"
                        data-testid={`marketing-empty-${s.key}`}
                      >
                        <ImageIcon className="w-6 h-6 mx-auto opacity-60 mb-2" />
                        <div className="text-[12.5px] text-slate-500 font-medium">No campaigns yet</div>
                        <div className="text-[11px] mt-0.5">Click <span className="text-slate-700 font-semibold">Add</span> to launch a new one.</div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {list.map((it) => (
                          <CampaignCard
                            key={it.id}
                            slot={s}
                            item={it}
                            onEdit={() => setEditor({ slot: s.key, id: it.id })}
                            onToggle={(v) => updateItem(s.key, it.id, { is_active: v })}
                            onRemove={() => removeItem(s.key, it.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Editor sheet */}
      {editor && openItem && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="marketing-editor">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setEditor(null)} />
          <aside className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-[0_24px_48px_-16px_rgba(15,23,42,0.35)] p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Editing</div>
                <div className="text-[15px] font-semibold text-slate-900 truncate">{SLOTS.find((s) => s.key === editor.slot)?.label}</div>
              </div>
              <button
                type="button"
                onClick={() => setEditor(null)}
                className="sa-btn-ghost w-8 h-8 grid place-items-center"
                data-testid="marketing-editor-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Image</div>
                <ImageUpload
                  value={openItem.image}
                  onChange={(v) => updateItem(editor.slot, editor.id, { image: v })}
                  label=""
                  aspect={SLOTS.find((s) => s.key === editor.slot)?.aspect || "banner"}
                  testId={`marketing-editor-img-${editor.slot}`}
                />
              </div>
              <SheetField label="Title">
                <Input value={openItem.title} onChange={(e) => updateItem(editor.slot, editor.id, { title: e.target.value })} placeholder="Weekend Sale" className="h-10 rounded-xl border-slate-200 bg-white" data-testid="marketing-editor-title" />
              </SheetField>
              <SheetField label="Subtitle">
                <Input value={openItem.subtitle} onChange={(e) => updateItem(editor.slot, editor.id, { subtitle: e.target.value })} placeholder="Up to 50% off" className="h-10 rounded-xl border-slate-200 bg-white" data-testid="marketing-editor-subtitle" />
              </SheetField>
              <SheetField label="Link URL">
                <Input value={openItem.link_to} onChange={(e) => updateItem(editor.slot, editor.id, { link_to: e.target.value })} placeholder="/shop?tag=offers" className="h-10 rounded-xl border-slate-200 bg-white font-mono text-[12.5px]" data-testid="marketing-editor-link" />
              </SheetField>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div>
                  <div className="text-[12.5px] font-semibold text-slate-900">Show on storefront</div>
                  <div className="text-[11px] text-slate-400">Toggle live visibility</div>
                </div>
                <Switch
                  checked={!!openItem.is_active}
                  onCheckedChange={(v) => updateItem(editor.slot, editor.id, { is_active: v })}
                  data-testid="marketing-editor-active"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <StatBlock icon={Eye}                label="Impressions" value={openItem.view_count || 0} />
                <StatBlock icon={MousePointerClick} label="Clicks"      value={openItem.click_count || 0} />
              </div>
              <div className="pt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => { removeItem(editor.slot, editor.id); setEditor(null); }}
                  className="flex-1 h-10 rounded-xl border border-red-200 text-red-600 text-[12.5px] font-semibold hover:bg-red-50 inline-flex items-center justify-center gap-1.5"
                  data-testid="marketing-editor-delete"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="sa-btn-primary h-10 px-5 text-[13px] flex-1"
                  data-testid="marketing-editor-done"
                >
                  Done
                </button>
              </div>
              <div className="text-[11px] text-slate-400 pt-2">
                Hit <span className="text-slate-700 font-semibold">Save all</span> in the top bar to publish live.
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function CampaignCard({ slot, item, onEdit, onToggle, onRemove }) {
  const aspect = slot.aspect === "square" ? "aspect-square" : "aspect-[16/6]";
  return (
    <div className="sa-card overflow-hidden group" data-testid={`marketing-card-${slot.key}-${item.id}`}>
      <div className={`relative w-full ${aspect} bg-slate-100`} onClick={onEdit} role="button">
        {item.image ? (
          <img src={item.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${slot.accent} opacity-90 grid place-items-center`}>
            <ImageIcon className="w-8 h-8 text-white/80" />
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full backdrop-blur ${item.is_active ? "bg-emerald-500/95 text-white" : "bg-slate-900/80 text-white"}`}>
            {item.is_active ? "ACTIVE" : "PAUSED"}
          </span>
        </div>
        {/* Hover actions */}
        <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors flex items-end justify-end p-2 gap-1.5 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="h-7 px-2.5 rounded-lg bg-white text-slate-900 text-[11px] font-semibold hover:bg-slate-50"
            data-testid={`marketing-card-edit-${item.id}`}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="h-7 w-7 grid place-items-center rounded-lg bg-white text-red-600 hover:bg-red-50"
            data-testid={`marketing-card-del-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-slate-900 truncate">{item.title || <span className="text-slate-400">Untitled campaign</span>}</div>
            <div className="text-[11px] text-slate-500 truncate">{item.subtitle || item.link_to || "—"}</div>
          </div>
          <Switch
            checked={!!item.is_active}
            onCheckedChange={onToggle}
            data-testid={`marketing-card-toggle-${item.id}`}
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-[10.5px] font-mono">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 border border-slate-100 text-slate-600">
            <Eye className="w-3 h-3" /> {item.view_count || 0}
          </span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 border border-slate-100 text-slate-600">
            <MousePointerClick className="w-3 h-3" /> {item.click_count || 0}
          </span>
          <span className="ml-auto text-slate-400 text-[10px] uppercase tracking-widest">{slot.group}</span>
        </div>
      </div>
    </div>
  );
}

function SheetField({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function StatBlock({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1 inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-[18px] font-semibold text-slate-900 font-mono">{value}</div>
    </div>
  );
}
